import { Button, Dropdown, Icon, Spinner } from "@deligoo/ui"
import clsx from "clsx"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { shallowEqual } from "react-redux"
import { useHistory } from "react-router"

import { useAppSelector } from "@/App"
import { Main } from "@/components/AppLayout"
import { NotificationsList } from "@/components/NotificationsList"
import { selectNotifications } from "@/store/notifications"
import {
  selectAreAllSelectedTeamsStatsLoaded,
  selectMultiTeamsTotalDeliveryOrdersCount,
  selectSortedSelectedTeamsWithStats,
} from "@/store/teamsStats"
import { useSaveUserSettings, useUserSettings } from "@/utils"
import { EventsTableHeader } from "@/views/EventsView"

import { getPriorityReturnValue } from "../EventsList/sortEventsListColumn"
import { MultiTeamSelectModal } from "../MultiTeamSelectModal"
import { StatisticsFilterModal } from "../StatisticsFilterModal"
import cls from "./EventsStatistics.module.scss"
import { TeamsStats, TeamWithStats } from "./TeamStats"

function selectedTeamsStatsPrioritySorter(a: TeamWithStats, b: TeamWithStats) {
  if (a.stats && b.stats) {
    const priorityReturnValue = getPriorityReturnValue(a.stats, b.stats)
    if (priorityReturnValue !== 0) return priorityReturnValue
  }

  return a.team.name.localeCompare(b.team.name, "pl")
}

function selectedTeamsStatsSorter(a: TeamWithStats, b: TeamWithStats) {
  return a.team.name.localeCompare(b.team.name, "pl")
}

type View = "only_teams" | "only_list"

type Settings = {
  selectedView: View
  hiddenColumns: EventsTableHeader[]
  sortTeamsByPriority: boolean
}

export const EventsStatistics = () => {
  const settings = useUserSettings<Settings>("events_statistics_settings")
  const [sortTeamsByPriority, setSortTeamsByPriority] = useState(settings?.sortTeamsByPriority || false)
  const teamsWithStatsSorter = useMemo(
    () => (sortTeamsByPriority ? selectedTeamsStatsPrioritySorter : selectedTeamsStatsSorter),
    [sortTeamsByPriority]
  )

  const selectedTeams = useAppSelector((state) => state.teams.currentMulti, shallowEqual)
  const selectedTeamsWithStats = useAppSelector(selectSortedSelectedTeamsWithStats(teamsWithStatsSorter))
  const notifications = useAppSelector(selectNotifications(selectedTeams?.map((team) => team.id)))
  const totalDeliveryOrdersCount = useAppSelector(selectMultiTeamsTotalDeliveryOrdersCount)
  const areAllStatsLoaded = useAppSelector(selectAreAllSelectedTeamsStatsLoaded)

  const [isTeamsSelectOpen, setTeamsSelectOpen] = useState(false)
  const [selectedView, setSelectedView] = useState<View>("only_teams")
  const [isStatisticsFilterModalOpen, setStatisticsFilterModalOpen] = useState(false)
  const [hiddenColumns, setHiddenColumns] = useState<EventsTableHeader[]>(settings?.hiddenColumns || [])

  useSaveUserSettings<Settings>("events_statistics_settings", { selectedView, hiddenColumns, sortTeamsByPriority })

  const { push } = useHistory()

  const { t } = useTranslation()

  return (
    <Main wrapperClassName={cls.eventsStatisticsPage} page>
      <nav className={cls.nav}>
        <div className={cls.controls}>
          <Button
            color="light"
            size="large"
            className={clsx(cls.navButton, cls.teams)}
            iconProps={{ name: "team", color: "primary" }}
            onClick={() => setTeamsSelectOpen(true)}
            pill
          >
            {`${selectedTeams.length} ${t("common:teams", {
              count: selectedTeams.length,
            })}`}
          </Button>

          <div className="hide-mobile">
            <Dropdown
              trigger={
                <span>
                  <Button color="light" size="large" className={cls.navButton} style={{ minWidth: "200px" }} pill>
                    <span className="text-muted">{t("show")}:</span> {t(selectedView)}
                    <Icon name="chevron-tiny-bold-down" color="muted" />
                  </Button>
                </span>
              }
            >
              <Dropdown.MenuItem
                handleClick={() => setSelectedView("only_teams")}
                color={selectedView === "only_teams" ? "primary" : "muted"}
              >
                {t("only_teams")}
              </Dropdown.MenuItem>
              <Dropdown.MenuItem
                handleClick={() => setSelectedView("only_list")}
                color={selectedView === "only_list" ? "primary" : "muted"}
              >
                {t("only_list")}
              </Dropdown.MenuItem>
            </Dropdown>
          </div>

          {selectedView === "only_teams" && (
            <Button
              color="light"
              size="large"
              className={clsx(cls.navButton, "hide-mobile")}
              iconProps={{ name: "filters", color: "primary" }}
              onClick={() => setStatisticsFilterModalOpen(true)}
              pill
            />
          )}
        </div>

        <div className={clsx(cls.controls, "hide-mobile")}>
          <div className={cls.totalOrders}>
            <span>
              {t("total_orders")}: <strong>{totalDeliveryOrdersCount || 0}</strong>
            </span>
            {areAllStatsLoaded || <Spinner className="ml-2" size="small" inline />}
          </div>
        </div>

        <div className={cls.controls}>
          <span className={cls.viewType}>{t("view_type")}:</span>

          <Button
            color="light"
            size="large"
            iconSide="right"
            iconProps={{ name: "grid", color: "primary" }}
            className={clsx(cls.navButton, "hide-mobile")}
            onClick={() => push("/centrum-zdarzen")}
            pill
          >
            {t("grid")}
          </Button>

          <Button
            color="primary"
            size="large"
            iconSide="right"
            iconProps={{ name: "statistics" }}
            className={clsx(cls.navButton, cls.active)}
            onClick={() => push("/centrum-zdarzen/statystyki")}
            pill
          >
            {t("statistics")}
          </Button>

          <Button
            color="light"
            size="large"
            iconSide="right"
            iconProps={{ name: "list-3", color: "primary" }}
            className={cls.navButton}
            onClick={() => push("/centrum-zdarzen/lista")}
            pill
          >
            {t("list")}
          </Button>
        </div>
      </nav>

      <div className={clsx(cls.controls, cls.amount, "hide-desktop")}>
        <div className={cls.totalOrders}>
          <span>
            {t("total_orders")}: <strong>{totalDeliveryOrdersCount || 0}</strong>
          </span>
          {areAllStatsLoaded || <Spinner className="ml-2" size="small" inline />}
        </div>
      </div>

      {isTeamsSelectOpen && <MultiTeamSelectModal setIsOpen={setTeamsSelectOpen} />}

      {isStatisticsFilterModalOpen && (
        <StatisticsFilterModal
          handleClose={() => setStatisticsFilterModalOpen(false)}
          hiddenColumns={hiddenColumns}
          setHiddenColumns={setHiddenColumns}
          sortTeamsByPriority={sortTeamsByPriority}
          setSortTeamsByPriority={setSortTeamsByPriority}
        />
      )}

      {selectedView !== "only_list" && (
        <section className={cls.stats}>
          {selectedTeamsWithStats.map((teamWithStats) => (
            <TeamsStats hiddenColumns={hiddenColumns} teamWithStats={teamWithStats} key={teamWithStats.team.uuid} />
          ))}
        </section>
      )}

      {selectedView !== "only_teams" && (
        <section className={cls.notifications}>
          <NotificationsList
            notifications={notifications}
            notificationsStatus="fetched" // TODO
          />
        </section>
      )}
    </Main>
  )
}
